import { GetServerSideProps } from 'next';
import { getApolloClient } from '@/lib/ApolloClient';
import getQueryParams from '@/utils/getQueryParams';
import { SERVER_FEATURE_FLAGS, UI_FEATURE_FLAGS } from '@/constants';
import getDeviceCharacteristics from '@/utils/getDeviceCharacteristics';
import reqHeaderToEntriesObj from '@/utils/reqHeaderToEntriesObj';
import { seoActivitySegmentationListingPage as seoSegmentationListingPageQuery } from '@/API/seoActivitySegmentationListingPage';
import {
  seoActivitySegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as seoActivitySegmentationSearchResultsSuccessType,
  seoActivitySegmentationListingPage as seoActivitySegmentationListingPageType,
  seoActivitySegmentationListingPage_seoSegmentationActivityDetails_SEOSegmentationActivityDetailsSuccess as seoSegmentationActivityDetailsType,
} from '@/__generated__/seoActivitySegmentationListingPage';
import { EventsSortBy, SEOSegmentationFilterInput } from '@/__generated__/globalTypes';
import { FeatureFlags } from '@/types/common';
import { getProviderBannerVisibility } from './helpers/getProviderBannerVisibility';

export interface ActivityListingPageProps {
  initialPageNumber: number;
  seoSegmentId: string;
  ipAddress: string;
  botInfo: string;
  userGeoInfo: string;
  userAgent: string;
  pageData: {
    seoSegmentationSearchResults: NonNullable<seoActivitySegmentationSearchResultsSuccessType>;
    seoSegmentationActivityDetails: NonNullable<
      seoSegmentationActivityDetailsType['segmentDetails']
    >;
    seoSegmentationBreadcrumbs: seoActivitySegmentationListingPageType['seoSegmentationBreadcrumbs'];
    getSEOSegmentationFAQs: seoActivitySegmentationListingPageType['getSEOSegmentationFAQs'];
    getSEOSegmentationLinkSections: seoActivitySegmentationListingPageType['getSEOSegmentationLinkSections'];
    seoNavigationLinkSection: seoActivitySegmentationListingPageType['seoNavigationLinkSection'];
    seoArticlesList: seoActivitySegmentationListingPageType['seoArticlesList'];
    seoActivityCategoryLinkSections: seoActivitySegmentationListingPageType['seoActivityCategoryLinkSections'];
  };
  deviceChar: {
    [key: string]: string;
  };
  local?: boolean;
  ldClientFlags: Record<string, any>;
  showProviderSignUpBanner: boolean;
}

const isLocal = process.env.NODE_ENV === 'development';

const getClientFeatureFlags = async (req: Parameters<GetServerSideProps>[0]['req']) => {
  const flagsToFetch = [
    UI_FEATURE_FLAGS.OHPN_DIRECTORY_SORT_BY,
    UI_FEATURE_FLAGS.OHPN_FILTER_BY_CAMPS,
    UI_FEATURE_FLAGS.PROVIDER_SIGNUP_BANNER,
    UI_FEATURE_FLAGS.OOH_ACTIVITIES_COOKING_CATEGORY,
  ];

  const { ldClient, ldUser } = req.careContext;
  const ldClientFlags: FeatureFlags = {};

  if (ldClient && ldUser) {
    const promises = flagsToFetch.map(async (flag) => {
      ldClientFlags[flag.id] = await ldClient.variationDetail(flag.id, ldUser, undefined);
    });
    await Promise.all(promises);
  }

  return ldClientFlags;
};

const flaggedFilterParams = (isDirectorySortEnabled: boolean, zipcode?: string) => {
  let filters: SEOSegmentationFilterInput | null = zipcode ? { zipcode } : null;

  if (isDirectorySortEnabled) {
    filters = {
      sortBy: EventsSortBy.RECOMMENDED,
      ...(zipcode ? { zipcode } : {}),
    };
  }

  return filters;
};

const getListPageData: GetServerSideProps = async ({ query, req, res }) => {
  const startPageGSSP = performance.now();
  const apolloOptions = {
    logError: req.log.error.bind(req.log),
    logInfo: req.log.info.bind(req.log),
  };
  const startApolloClient = performance.now();
  const apolloClient = getApolloClient(apolloOptions);
  const stopApolloClient = performance.now();
  const serverFeatureFlags = req.careContext?.ldServerFlags;
  const ldClientFlags = await getClientFeatureFlags(req);
  const userAgent = req.headers['user-agent'] ?? '';
  const deviceCharHeader = req.headers['x-akamai-device-characteristics']?.toString() ?? '';
  const deviceChar = isLocal
    ? getDeviceCharacteristics(userAgent, deviceCharHeader)
    : reqHeaderToEntriesObj(deviceCharHeader, ';');

  const { seoSegmentId, pageNumber, local, location } = getQueryParams(query);
  const zipcode = query.zipCode;
  const seoSegmentIdNew = `/explore/d${seoSegmentId}`;

  const botInfo = req.headers['Akamai-Bot']?.toString() ?? '';
  const userGeoInfo = req.headers['x-akamai-edgescape']?.toString() ?? '';
  const ipAddressArray = req.headers['x-forwarded-for'];
  let ipAddress;
  if (typeof ipAddressArray !== 'undefined') {
    [ipAddress] = ipAddressArray.toString().split(',');
  } else {
    ipAddress = '';
  }
  const startGetPageData = performance.now();
  if (serverFeatureFlags?.[SERVER_FEATURE_FLAGS.SEO_MFE_LOG_AKAMAI_HEADER]?.value) {
    req.log.info({
      event: 'x-akamai-edgescape header',
      seoSegmentId,
      userAgent,
      deviceChar,
      ipAddress,
      akamaiHeader: userGeoInfo,
    });
  }

  const isDirectorySortEnabled = ldClientFlags[UI_FEATURE_FLAGS.OHPN_DIRECTORY_SORT_BY.id]?.value;

  const seoSegmentationListingPage = await seoSegmentationListingPageQuery(apolloClient, {
    filters: flaggedFilterParams(
      isDirectorySortEnabled,
      typeof zipcode === 'string' ? zipcode : undefined
    ),
    pageNumber,
    seoSegmentId: seoSegmentIdNew,
    ipAddress,
    botInfo,
    userGeoInfo,
    userAgent,
    local,
    locationId: location,
  });
  const stopGetPageData = performance.now();

  if (seoSegmentationListingPage.error !== null) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId: seoSegmentIdNew,
      userAgent,
      deviceChar,
      query,
      res,
      errors: seoSegmentationListingPage.error,
    });

    return {
      notFound: true,
    };
  }

  const seoSegmentationListingPageData = seoSegmentationListingPage.queryResponse.data;

  if (
    seoSegmentationListingPageData.seoSegmentationActivityDetails.__typename ===
      'SEOSegmentationActivityDetailsError' ||
    seoSegmentationListingPageData.seoSegmentationSearchResults.__typename ===
      'SEOSegmentationSearchResultsError' ||
    seoSegmentationListingPage.queryResponse.errors
  ) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId: seoSegmentIdNew,
      userAgent,
      deviceChar,
      query,
      res,
      errors: seoSegmentationListingPage.queryResponse.errors,
    });

    return {
      notFound: true,
    };
  }

  if (seoSegmentationListingPageData.seoSegmentationActivityDetails.segmentDetails === null) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId: seoSegmentIdNew,
      userAgent,
      deviceChar,
      query,
      res,
      errorMsg: 'ERROR - seoSegmentationActivityDetails is null',
    });

    return {
      notFound: true,
    };
  }

  const showProviderSignUpBanner = getProviderBannerVisibility(req, ldClientFlags);

  const pageProps: { props: ActivityListingPageProps } = {
    props: {
      initialPageNumber: pageNumber,
      seoSegmentId: seoSegmentIdNew,
      ipAddress,
      botInfo,
      userGeoInfo,
      userAgent,
      pageData: {
        seoSegmentationSearchResults: seoSegmentationListingPageData.seoSegmentationSearchResults,
        seoSegmentationActivityDetails:
          seoSegmentationListingPageData.seoSegmentationActivityDetails.segmentDetails,
        seoSegmentationBreadcrumbs: seoSegmentationListingPageData.seoSegmentationBreadcrumbs,
        getSEOSegmentationFAQs: seoSegmentationListingPageData.getSEOSegmentationFAQs,
        getSEOSegmentationLinkSections:
          seoSegmentationListingPageData.getSEOSegmentationLinkSections,
        seoNavigationLinkSection: seoSegmentationListingPageData.seoNavigationLinkSection ?? null,
        seoArticlesList: seoSegmentationListingPageData.seoArticlesList,
        seoActivityCategoryLinkSections:
          seoSegmentationListingPageData.seoActivityCategoryLinkSections,
      },
      deviceChar,
      local,
      ldClientFlags,
      showProviderSignUpBanner,
    },
  };

  req.log.info({
    event: 'SeoListingPageLoadSuccess',
    deviceChar,
    query,
    res,
  });
  const stopPageGSSP = performance.now();
  req.careContext.serverTimings.pageGSSP = stopPageGSSP - startPageGSSP;
  req.careContext.serverTimings.apolloClient = stopApolloClient - startApolloClient;
  req.careContext.serverTimings.pageData = stopGetPageData - startGetPageData;
  return pageProps;
};
export default getListPageData;
